import React, { useEffect, useState, useMemo } from "react";
import {
  listarLicitaciones,
  aceptarLicitacion,
  rechazarLicitacion,
  aceptarLicitacionesMasivas,
  rechazarLicitacionesMasivas,
} from "../api";
import {
  HiCheck,
  HiX,
  HiOutlineInbox,
  HiOutlineSearch,
  HiOutlineFilter,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiRefresh,
  HiSelector,
  HiSortAscending,
  HiSortDescending,
} from "react-icons/hi";
import PendienteCard from "./PendienteCard";
import SkeletonCard from "./SkeletonCard";
import ModalConfirmacion from "./ModalConfirmacion";
import { toast } from "react-toastify";
import "../styles.css";

function Pendientes({
  usuario,
  onActualizarGlobal,
  triggerActualizar,
  paises = [],
}) {
  const [licitaciones, setLicitaciones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [filtroPais, setFiltroPais] = useState("");
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [showConfirmMasivo, setShowConfirmMasivo] = useState(false);
  const [accionMasiva, setAccionMasiva] = useState(null); // "aceptar" | "rechazar"
  const [procesandoMasivo, setProcesandoMasivo] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "fecha_presentacion",
    direction: "asc",
  });

  const fetchPendientes = async () => {
    if (!usuario) return;
    try {
      setCargando(true);
      const data = await listarLicitaciones();
      const pendientes = (data || []).filter(
        (l) => (l.estado || "").toLowerCase() === "pendiente de aceptar"
      );

      setLicitaciones(pendientes);
      setSeleccionados(new Set());
    } catch (err) {
      console.error("Error cargando licitaciones pendientes:", err);
      toast.error("Error al cargar licitaciones pendientes");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    fetchPendientes();
  }, [usuario, triggerActualizar]);

  useEffect(() => {
    setPage(0);
  }, [busqueda, filtroPais, pageSize, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return {
        key,
        direction: key === "presupuesto" ? "desc" : "asc",
      };
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <HiSelector className="sort-icon inactive" size={15} />;
    }
    return sortConfig.direction === "asc" ? (
      <HiSortAscending className="sort-icon active" size={15} />
    ) : (
      <HiSortDescending className="sort-icon active" size={15} />
    );
  };

  const licitacionesFiltradas = useMemo(() => {
    const filtradas = licitaciones
      .filter((l) => {
        if (!filtroPais) return true;
        return (
          (l.pais || "").trim().toLowerCase() ===
          filtroPais.trim().toLowerCase()
        );
      })
      .filter((l) => {
        if (!busqueda) return true;
        const q = busqueda.toLowerCase();
        const exp = (l.numero_expediente || "").toLowerCase();
        const tit = (l.titulo || "").toLowerCase();
        const org = (l.organismo || "").toLowerCase();
        const pto = l.presupuesto != null ? l.presupuesto.toString() : "";
        return (
          exp.includes(q) ||
          tit.includes(q) ||
          org.includes(q) ||
          pto.includes(q)
        );
      });

    const dir = sortConfig.direction === "asc" ? 1 : -1;
    const key = sortConfig.key || "fecha_presentacion";

    return [...filtradas].sort((a, b) => {
      if (key === "presupuesto") {
        const va = a.presupuesto != null ? Number(a.presupuesto) : null;
        const vb = b.presupuesto != null ? Number(b.presupuesto) : null;
        if (va === null && vb === null) return 0;
        if (va === null) return 1;
        if (vb === null) return -1;
        return (va - vb) * dir;
      }

      if (key === "fecha_presentacion") {
        const fa = a.fecha_presentacion
          ? new Date(a.fecha_presentacion).getTime()
          : null;
        const fb = b.fecha_presentacion
          ? new Date(b.fecha_presentacion).getTime()
          : null;
        if (fa === null && fb === null) return 0;
        if (fa === null) return 1;
        if (fb === null) return -1;
        return (fa - fb) * dir;
      }

      const sa = String(a[key] || "").trim();
      const sb = String(b[key] || "").trim();
      if (!sa && !sb) return 0;
      if (!sa) return 1;
      if (!sb) return -1;
      return (
        sa.localeCompare(sb, "es", { numeric: true, sensitivity: "base" }) *
        dir
      );
    });
  }, [licitaciones, filtroPais, busqueda, sortConfig]);

  const totalItems = licitacionesFiltradas.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const start = page * pageSize;
  const end = start + pageSize;
  const currentPageItems = licitacionesFiltradas.slice(start, end);

  const toggleSeleccionar = (numero) => {
    setSeleccionados((prev) => {
      const nuevo = new Set(prev);
      if (nuevo.has(numero)) {
        nuevo.delete(numero);
      } else {
        nuevo.add(numero);
      }
      return nuevo;
    });
  };

  const seleccionarTodosVisibles = () => {
    if (
      currentPageItems.every((l) => seleccionados.has(l.numero_expediente))
    ) {
      setSeleccionados((prev) => {
        const nuevo = new Set(prev);
        currentPageItems.forEach((l) => nuevo.delete(l.numero_expediente));
        return nuevo;
      });
    } else {
      setSeleccionados((prev) => {
        const nuevo = new Set(prev);
        currentPageItems.forEach((l) => nuevo.add(l.numero_expediente));
        return nuevo;
      });
    }
  };

  const todosVisiblesSeleccionados =
    currentPageItems.length > 0 &&
    currentPageItems.every((l) => seleccionados.has(l.numero_expediente));

  const handleAceptarIndividual = async (numero) => {
    try {
      await aceptarLicitacion(numero);
      toast.success(
        `Expediente ${numero.toUpperCase()} trasladado a Licitaciones`
      );
      setLicitaciones((prev) =>
        prev.filter(
          (l) => l.numero_expediente.toLowerCase() !== numero.toLowerCase()
        )
      );
      setSeleccionados((prev) => {
        const nuevo = new Set(prev);
        nuevo.delete(numero);
        return nuevo;
      });
      if (typeof onActualizarGlobal === "function") onActualizarGlobal();
    } catch (err) {
      toast.error(err.message || "Error al aceptar la licitación");
    }
  };

  const handleRechazarIndividual = async (numero) => {
    try {
      await rechazarLicitacion(numero);
      toast.info(`Expediente ${numero.toUpperCase()} rechazado y archivado`);
      setLicitaciones((prev) =>
        prev.filter(
          (l) => l.numero_expediente.toLowerCase() !== numero.toLowerCase()
        )
      );
      setSeleccionados((prev) => {
        const nuevo = new Set(prev);
        nuevo.delete(numero);
        return nuevo;
      });
      if (typeof onActualizarGlobal === "function") onActualizarGlobal();
    } catch (err) {
      toast.error(err.message || "Error al rechazar la licitación");
    }
  };

  const abrirConfirmacionMasiva = (accion) => {
    setAccionMasiva(accion);
    setShowConfirmMasivo(true);
  };

  const ejecutarAccionMasiva = async () => {
    setShowConfirmMasivo(false);
    const listaNumeros = Array.from(seleccionados);
    if (!listaNumeros.length) return;

    setProcesandoMasivo(true);
    const toastId = toast.loading(
      `Procesando ${listaNumeros.length} licitaciones...`
    );
    try {
      if (accionMasiva === "aceptar") {
        await aceptarLicitacionesMasivas(listaNumeros);
        toast.update(toastId, {
          render: `✅ ${listaNumeros.length} licitaciones aceptadas y trasladadas a Licitaciones`,
          type: "success",
          isLoading: false,
          autoClose: 3500,
        });
      } else if (accionMasiva === "rechazar") {
        await rechazarLicitacionesMasivas(listaNumeros);
        toast.update(toastId, {
          render: `🗑️ ${listaNumeros.length} licitaciones rechazadas`,
          type: "info",
          isLoading: false,
          autoClose: 3500,
        });
      }

      await fetchPendientes();
      if (typeof onActualizarGlobal === "function") onActualizarGlobal();
    } catch (err) {
      toast.update(toastId, {
        render: err.message || "Error al procesar las licitaciones",
        type: "error",
        isLoading: false,
        autoClose: 4000,
      });
    } finally {
      setProcesandoMasivo(false);
      setSeleccionados(new Set());
    }
  };

  return (
    <div className="pendientes-container animate-fadeIn">
      {/* Header */}
      <div className="dashboard-header pendientes-header">
        <div>
          <div className="header-title-row">
            <h1>Licitaciones Pendientes</h1>
            <span className="badge-contador-pendientes">
              {licitaciones.length} por revisar
            </span>
          </div>
          <p className="dashboard-subtitle">
            Triaje de nuevas licitaciones cargadas: acéptalas para trasladarlas
            a la lista en curso o descártalas.
          </p>
        </div>

        <div className="header-actions">
          <button
            className="btn-refrescar"
            onClick={fetchPendientes}
            title="Recargar lista"
            disabled={cargando}
          >
            <HiRefresh size={20} className={cargando ? "spin-animation" : ""} />
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      {/* Barra de Filtros y Búsqueda */}
      <div className="pendientes-toolbar">
        <div className="toolbar-search">
          <HiOutlineSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por expediente, título u organismo..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          {busqueda && (
            <button
              className="btn-clear-search"
              onClick={() => setBusqueda("")}
            >
              ✕
            </button>
          )}
        </div>

        <div className="toolbar-filtros">
          <div className="select-wrapper">
            <HiOutlineFilter className="filter-icon" />
            <select
              value={filtroPais}
              onChange={(e) => setFiltroPais(e.target.value)}
            >
              <option value="">Todos los países</option>
              {paises.map((p, idx) => (
                <option key={idx} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Barra de Acciones Masivas (cuando hay seleccionadas) */}
      {seleccionados.size > 0 && (
        <div className="barra-acciones-masivas animate-fadeIn">
          <div className="masivas-info">
            <span className="contador-seleccionados">{seleccionados.size}</span>
            <span>licitación(es) seleccionada(s)</span>
          </div>

          <div className="masivas-botones">
            <button
              className="btn-accion-masiva aceptar"
              onClick={() => abrirConfirmacionMasiva("aceptar")}
              disabled={procesandoMasivo}
            >
              <HiCheck size={18} />
              <span>Aceptar seleccionadas</span>
            </button>
            <button
              className="btn-accion-masiva rechazar"
              onClick={() => abrirConfirmacionMasiva("rechazar")}
              disabled={procesandoMasivo}
            >
              <HiX size={18} />
              <span>Rechazar seleccionadas</span>
            </button>
            <button
              className="btn-deseleccionar"
              onClick={() => setSeleccionados(new Set())}
            >
              Deseleccionar todas
            </button>
          </div>
        </div>
      )}

      {/* Encabezado de Lista con Ordenación */}
      <div className="lista-encabezado lista-encabezado-pendientes">
        <div className="col-checkbox">
          <input
            type="checkbox"
            checked={todosVisiblesSeleccionados}
            onChange={seleccionarTodosVisibles}
            title={
              todosVisiblesSeleccionados
                ? "Deseleccionar visibles"
                : "Seleccionar todas las visibles"
            }
          />
        </div>
        <div
          className={`col-expediente sortable-col ${
            sortConfig.key === "numero_expediente" ? "sorted-active" : ""
          }`}
          onClick={() => handleSort("numero_expediente")}
        >
          <span>Expediente</span>
          {renderSortIcon("numero_expediente")}
        </div>
        <div
          className={`col-titulo sortable-col ${
            sortConfig.key === "titulo" ? "sorted-active" : ""
          }`}
          onClick={() => handleSort("titulo")}
        >
          <span>Título</span>
          {renderSortIcon("titulo")}
        </div>
        <div
          className={`col-organismo sortable-col ${
            sortConfig.key === "organismo" ? "sorted-active" : ""
          }`}
          onClick={() => handleSort("organismo")}
        >
          <span>Organismo</span>
          {renderSortIcon("organismo")}
        </div>
        <div
          className={`col-presupuesto sortable-col ${
            sortConfig.key === "presupuesto" ? "sorted-active" : ""
          }`}
          onClick={() => handleSort("presupuesto")}
        >
          <span>Presupuesto</span>
          {renderSortIcon("presupuesto")}
        </div>
        <div
          className={`col-fecha sortable-col ${
            sortConfig.key === "fecha_presentacion" ? "sorted-active" : ""
          }`}
          onClick={() => handleSort("fecha_presentacion")}
        >
          <span>Fecha Límite</span>
          {renderSortIcon("fecha_presentacion")}
        </div>
        <div className="col-acciones-header">Acciones</div>
      </div>

      {/* Contenido de la Lista */}
      <div className="pendientes-lista">
        {cargando ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : currentPageItems.length === 0 ? (
          <div className="estado-vacio-pendientes">
            <div className="icono-vacio">
              <HiOutlineInbox size={48} />
            </div>
            <h3>No hay licitaciones pendientes</h3>
            {licitaciones.length > 0 && (
              <p>
                No hay licitaciones que coincidan con los filtros de búsqueda
                aplicados.
              </p>
            )}
          </div>
        ) : (
          currentPageItems.map((lic) => (
            <PendienteCard
              key={lic.numero_expediente}
              licitacion={lic}
              seleccionado={seleccionados.has(lic.numero_expediente)}
              onToggleSelect={toggleSeleccionar}
              onAceptar={handleAceptarIndividual}
              onRechazar={handleRechazarIndividual}
              usuario={usuario}
              onActualizar={fetchPendientes}
            />
          ))
        )}
      </div>

      {/* Paginación Moderna */}
      {!cargando && totalItems > 0 && (
        <div className="paginacion-moderna">
          <div className="paginacion-resumen">
            <span className="badge-rango">
              {start + 1} - {Math.min(end, totalItems)}
            </span>
            <span className="texto-total">
              de <b>{totalItems}</b> licitaciones
            </span>
          </div>

          <div className="paginacion-nav">
            <button
              className="btn-pag-nav"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              title="Página anterior"
            >
              <HiOutlineChevronLeft size={16} />
              <span>Anterior</span>
            </button>

            <div className="paginacion-pills">
              {page > 2 && (
                <>
                  <button className="pag-pill" onClick={() => setPage(0)}>
                    1
                  </button>
                  {page > 3 && <span className="pag-ellipsis">...</span>}
                </>
              )}

              {Array.from({ length: totalPages })
                .map((_, idx) => idx)
                .filter((idx) => Math.abs(idx - page) <= 2)
                .map((idx) => (
                  <button
                    key={idx}
                    className={`pag-pill ${idx === page ? "activo" : ""}`}
                    onClick={() => setPage(idx)}
                  >
                    {idx + 1}
                  </button>
                ))}

              {page < totalPages - 3 && (
                <>
                  {page < totalPages - 4 && (
                    <span className="pag-ellipsis">...</span>
                  )}
                  <button
                    className="pag-pill"
                    onClick={() => setPage(totalPages - 1)}
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>

            <button
              className="btn-pag-nav"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              title="Página siguiente"
            >
              <span>Siguiente</span>
              <HiOutlineChevronRight size={16} />
            </button>
          </div>

          <div className="paginacion-selector">
            <span className="label-selector">Por página</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      )}

      {/* Modal Confirmación Masiva */}
      <ModalConfirmacion
        visible={showConfirmMasivo}
        titulo={
          accionMasiva === "aceptar"
            ? "Aceptar Licitaciones Seleccionadas"
            : "Rechazar Licitaciones Seleccionadas"
        }
        mensaje={
          accionMasiva === "aceptar"
            ? `¿Deseas aceptar las ${seleccionados.size} licitaciones seleccionadas? Se trasladarán a la lista principal de Licitaciones en curso.`
            : `¿Deseas rechazar las ${seleccionados.size} licitaciones seleccionadas? Se marcarán como rechazadas y se mantendrán ocultas.`
        }
        tipo={accionMasiva === "aceptar" ? "verde" : "rojo"}
        onConfirmar={ejecutarAccionMasiva}
        onCancelar={() => setShowConfirmMasivo(false)}
      />
    </div>
  );
}

export default Pendientes;
